import React, { useEffect, useState } from "react";
import { useApolloClient, useQuery } from "@apollo/client";
import { useAuth0 } from "@auth0/auth0-react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  GET_WEBSITE_BRANDING,
  uploadTrustCenterFavicon,
  uploadTrustCenterFont,
  uploadTrustCenterHeroImage,
} from "../../../../apis/trust-center-apis/trustCenterApis";
import {
  GET_COMPANY_LOGO,
  uploadCompanyLogo,
} from "../../../../apis/company-logo-apis/companyLogoApis";
import {
  DialogHeader,
  dialogActionsSx,
  dialogPaperProps,
} from "../../components/DialogParts";
import {
  outlinedButtonSx,
  primaryButtonSx,
} from "../../../Organization/CodeSecurity/codeSecurityShared";
import {
  CUSTOM_FONT_KEY,
  fontOf,
  fontStack,
  googleFontUrl,
} from "../../data/brand";
import {
  WEBSITE_BRANDING_ITEMS,
  fileFromWebsiteFont,
  fileFromWebsiteImage,
  foundOnWebsite,
  initialWebsiteChoices,
  safeFontName,
  siteName,
  websiteFormChanges,
  websiteImageUrl,
} from "../../data/websiteBranding";

const errorOf = (error) => ({
  message:
    error?.graphQLErrors?.[0]?.message ||
    error?.message ||
    "Could not read that website. Try again.",
  // WEBSITE_BLOCKED: the site's bot protection turned the request away.
  code: error?.graphQLErrors?.[0]?.errorCode || null,
});

// The site's own font, shown in itself: its copied files as data: links (the
// dialog's own @font-face), or Google Fonts by name.
const PREVIEW_FONT = "Website font preview";
const FONT_FORMAT = {
  woff2: "woff2",
  woff: "woff",
  ttf: "truetype",
  otf: "opentype",
};
const WebsiteFontPreview = ({ customFont }) => {
  const css =
    customFont.source === "files"
      ? customFont.files
          .map(
            (f) =>
              `@font-face{font-family:"${PREVIEW_FONT}";src:url(data:font/${f.format};base64,${f.data}) format("${FONT_FORMAT[f.format]}");font-weight:${f.weight};font-style:${f.style};}`
          )
          .join("\n")
      : null;
  const family =
    customFont.source === "files"
      ? PREVIEW_FONT
      : safeFontName(customFont.name);
  return (
    <>
      {css && <style>{css}</style>}
      {!css && <link rel="stylesheet" href={googleFontUrl(family)} />}
      <Typography sx={{ fontFamily: `"${family}", sans-serif`, fontSize: 15 }}>
        {customFont.name}
      </Typography>
    </>
  );
};

// What a row shows next to its checkbox: a preview of what was found.
const Preview = ({ item, result }) => {
  const theme = useTheme();
  const box = {
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "8px",
    border: `1px solid ${theme.customColors.border.main}`,
    backgroundColor: theme.customColors.background.infoBox,
    overflow: "hidden",
  };
  const value = result[item.key];
  if (item.key === "fontFamily" && !value && result.customFont)
    return <WebsiteFontPreview customFont={result.customFont} />;
  if (!value) return null;
  if (["logo", "bannerImage", "favicon"].includes(item.key)) {
    const size =
      item.key === "favicon"
        ? { width: 40, height: 40 }
        : { width: 120, height: 48 };
    return (
      <Box sx={{ ...box, ...size }}>
        <Box
          component="img"
          src={websiteImageUrl(value)}
          alt={`${item.label} from your website`}
          sx={{
            width: "100%",
            height: "100%",
            objectFit: item.key === "bannerImage" ? "cover" : "contain",
          }}
        />
      </Box>
    );
  }
  if (item.key === "fontFamily") {
    const font = fontOf(value);
    return (
      <Typography sx={{ fontFamily: fontStack(font), fontSize: 15 }}>
        {font.name}
      </Typography>
    );
  }
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Box
        aria-hidden
        sx={{
          width: 24,
          height: 24,
          borderRadius: "6px",
          backgroundColor: value,
          border: `1px solid ${theme.customColors.border.main}`,
        }}
      />
      <Typography sx={{ fontFamily: "monospace", fontSize: 13 }}>
        {value}
      </Typography>
    </Stack>
  );
};

// The line under a row's label: what using it does, or why it is missing.
const noteFor = (key, result, hasLogo) => {
  const found = result[key];
  switch (key) {
    case "logo":
      if (!found) return "Not found on your website.";
      return hasLogo
        ? "Replaces your current logo. Saved straight away, also in Company Settings."
        : "Saved straight away. Also shown in Company Settings.";
    case "bannerImage":
      return found
        ? "The image your website shares on social media. Sets the banner style to Image."
        : "Not found on your website.";
    case "primaryColor":
      if (!found) return "Not found. The default colour stays.";
      return result.primaryColorFound
        ? `Your website uses ${result.primaryColorFound}. Made darker so white text and links stay readable.`
        : "Buttons and links.";
    case "secondaryColor":
      if (!found) return "Not found. Highlights use the primary colour.";
      return result.secondaryColorFound
        ? `Your website uses ${result.secondaryColorFound}. Made darker so it reads on white.`
        : "Highlights: the open tab, checkboxes and counts.";
    case "fontFamily":
      if (found) return "For all text on your public page.";
      if (result.customFont?.source === "google")
        return `Your website uses ${result.customFont.name} from Google Fonts. Used as a custom font.`;
      if (result.customFont?.source === "files")
        return `Copies ${result.customFont.files.length} font file${result.customFont.files.length === 1 ? "" : "s"} of ${result.customFont.name} from your website. Tick it only if your font licence lets you use it on your Trust Center.`;
      return result.fontName
        ? `Your website uses ${result.fontName}, but its font files could not be read. Upload them under Font → Custom font.`
        : "Not found. Your font stays as it is.";
    case "favicon":
      return found
        ? "The icon in the browser tab."
        : "Not found on your website.";
    default:
      return "";
  }
};

// Reads branding from the company website and lets the admin pick what to
// use. Colours and font go into the form; images are uploaded through the
// normal routes (banner and favicon join the draft, the logo is saved at
// once like an upload).
const FetchBrandingDialog = ({ open, website, onClose, onApply }) => {
  const theme = useTheme();
  const client = useApolloClient();
  const { getAccessTokenSilently, getIdTokenClaims } = useAuth0();
  const { data: logoData } = useQuery(GET_COMPANY_LOGO, { skip: !open });
  const hasLogo = !!logoData?.getCompanyLogo?.logoPath;

  const [url, setUrl] = useState(website || "");
  const [loading, setLoading] = useState(false);
  // { message, code } or null.
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [chosen, setChosen] = useState({});
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    if (!open) return;
    setUrl(website || "");
    setLoading(false);
    setError(null);
    setResult(null);
    setChosen({});
    setApplying(false);
  }, [open, website]);

  const handleFetch = async (event) => {
    event?.preventDefault();
    if (!url.trim()) {
      setError({
        message: "Enter your website address, like https://acme.com.",
      });
      return;
    }
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const { data } = await client.query({
        query: GET_WEBSITE_BRANDING,
        variables: { url: url.trim() },
        fetchPolicy: "no-cache",
      });
      const found = data.getWebsiteBranding;
      setResult(found);
      setChosen(initialWebsiteChoices(found, { hasLogo }));
    } catch (e) {
      setError(errorOf(e));
    } finally {
      setLoading(false);
    }
  };

  const anyChosen = Object.values(chosen).some(Boolean);
  const nothingFound =
    result &&
    WEBSITE_BRANDING_ITEMS.every(({ key }) => !foundOnWebsite(result, key));

  const handleApply = async () => {
    setApplying(true);
    setError(null);
    const changes = websiteFormChanges(result, chosen);
    const failed = [];
    let logoSaved = false;
    try {
      const accessToken = await getAccessTokenSilently();
      const idToken = (await getIdTokenClaims()).__raw;
      const tokens = { accessToken, idToken };
      const upload = (key, work) =>
        work().catch((e) => {
          const label = WEBSITE_BRANDING_ITEMS.find((i) => i.key === key).label;
          failed.push({ key, message: `${label}: ${e.message}` });
        });
      await Promise.all([
        chosen.bannerImage &&
          upload("bannerImage", async () => {
            const { heroImagePath } = await uploadTrustCenterHeroImage(
              fileFromWebsiteImage(result.bannerImage, "banner"),
              tokens
            );
            changes.heroImagePath = heroImagePath;
            changes.bannerStyle = "image";
          }),
        chosen.favicon &&
          upload("favicon", async () => {
            const { faviconPath } = await uploadTrustCenterFavicon(
              fileFromWebsiteImage(result.favicon, "favicon"),
              tokens
            );
            changes.faviconPath = faviconPath;
          }),
        chosen.fontFamily &&
          !result.fontFamily &&
          result.customFont?.source === "files" &&
          upload("fontFamily", async () => {
            const name = safeFontName(result.customFont.name);
            const files = [];
            for (const fontFile of result.customFont.files) {
              // eslint-disable-next-line no-await-in-loop
              const { fontPath } = await uploadTrustCenterFont(
                fileFromWebsiteFont(fontFile, name),
                tokens
              );
              files.push({
                path: fontPath,
                weight: fontFile.weight,
                style: String(fontFile.style).toLowerCase(),
                fileName: `${name} ${fontFile.weight}.${fontFile.format}`,
              });
            }
            Object.assign(changes, {
              fontFamily: CUSTOM_FONT_KEY,
              customFontName: name,
              customFontSource: "files",
              customFontFiles: files,
            });
          }),
        chosen.logo &&
          upload("logo", async () => {
            await uploadCompanyLogo(
              fileFromWebsiteImage(result.logo, "logo"),
              tokens
            );
            logoSaved = true;
            // Every screen that shows the logo picks it up.
            await client.refetchQueries({
              include: ["getCompanyLogo", "getTrustCenter"],
            });
          }),
      ]);
    } catch (e) {
      failed.push({
        key: null,
        message: e.message || "Could not sign you in.",
      });
    }
    if (Object.keys(changes).length > 0 || logoSaved)
      onApply(changes, result, { logoSaved });
    setApplying(false);
    if (failed.length === 0) {
      onClose();
      return;
    }
    // Keep only what failed ticked, so trying again does not redo the rest.
    const failedKeys = failed.map((f) => f.key);
    setChosen((prev) =>
      Object.fromEntries(
        Object.keys(prev).map((key) => [
          key,
          prev[key] && failedKeys.includes(key),
        ])
      )
    );
    setError({
      message: `Some items could not be used. ${failed
        .map((f) => f.message)
        .join(" ")}`,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={applying ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={dialogPaperProps}
    >
      <DialogHeader
        title="Fetch branding from your website"
        subtitle="We read your home page for your logo, banner image, colours, font and favicon. Pick what to use, then check the preview and save."
        onClose={applying ? undefined : onClose}
      />
      <DialogContent dividers>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <Box component="form" onSubmit={handleFetch} noValidate>
            <Typography
              component="label"
              htmlFor="website-branding-url"
              sx={{
                display: "block",
                fontSize: "0.875rem",
                fontWeight: 600,
                color: theme.customColors.text.strong,
                mb: 0.75,
              }}
            >
              Website
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              <TextField
                id="website-branding-url"
                size="small"
                fullWidth
                placeholder="https://yourcompany.com"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                disabled={loading || applying}
                inputProps={{ maxLength: 2048 }}
              />
              <Button
                type="submit"
                variant="outlined"
                disabled={loading || applying}
                sx={{ ...outlinedButtonSx(theme), flexShrink: 0 }}
              >
                {loading ? "Fetching…" : result ? "Fetch again" : "Fetch"}
              </Button>
            </Stack>
            <Typography
              variant="caption"
              sx={{
                display: "block",
                mt: 0.75,
                color: theme.customColors.text.secondary,
              }}
            >
              {website
                ? "Your website from Company Settings. You can use another page."
                : "Your public home page works best."}
            </Typography>
          </Box>

          {loading && (
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              role="status"
            >
              <CircularProgress size={18} />
              <Typography
                variant="body2"
                sx={{ color: theme.customColors.text.secondary }}
              >
                Reading {siteName(url.trim())}… this can take a few seconds.
              </Typography>
            </Stack>
          )}

          {error?.code === "WEBSITE_BLOCKED" && (
            <Alert severity="warning" sx={{ borderRadius: "8px" }}>
              <AlertTitle sx={{ fontWeight: 600 }}>
                {siteName(
                  url.trim().includes("://")
                    ? url.trim()
                    : `https://${url.trim()}`
                )}{" "}
                blocked the request
              </AlertTitle>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {error.message} Nothing is wrong with your Trust Center.
              </Typography>
              <Typography variant="body2" component="div">
                You can:
                <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
                  <li>
                    Try another page of your website, like your About page.
                  </li>
                  <li>
                    Ask whoever runs your website to allow the visitor named
                    “RegoditBrandingBot”, then fetch again.
                  </li>
                  <li>
                    Close this and set your logo, colours and font by hand.
                  </li>
                </Box>
              </Typography>
            </Alert>
          )}
          {error && error.code !== "WEBSITE_BLOCKED" && (
            <Alert severity="error" sx={{ borderRadius: "8px" }}>
              {error.message}
            </Alert>
          )}

          {result && nothingFound && (
            <Alert severity="info" sx={{ borderRadius: "8px" }}>
              Nothing usable was found on {siteName(result.url)}. Fill in your
              branding by hand.
            </Alert>
          )}

          {result && !nothingFound && (
            <Box>
              <Typography
                sx={{ fontWeight: 600, color: theme.customColors.text.strong }}
              >
                Found on {siteName(result.url)}
              </Typography>
              <Box
                role="list"
                sx={{
                  mt: 1,
                  border: `1px solid ${theme.customColors.border.main}`,
                  borderRadius: "12px",
                  overflow: "hidden",
                }}
              >
                {WEBSITE_BRANDING_ITEMS.map((item, index) => {
                  const found = foundOnWebsite(result, item.key);
                  const id = `website-branding-${item.key}`;
                  return (
                    <Stack
                      key={item.key}
                      role="listitem"
                      direction="row"
                      spacing={1.5}
                      alignItems="center"
                      sx={{
                        px: 1.5,
                        py: 1.25,
                        borderTop:
                          index === 0
                            ? "none"
                            : `1px solid ${theme.customColors.border.main}`,
                        opacity: found ? 1 : 0.7,
                      }}
                    >
                      <Checkbox
                        size="small"
                        checked={!!chosen[item.key]}
                        disabled={!found || applying}
                        onChange={(e) =>
                          setChosen((prev) => ({
                            ...prev,
                            [item.key]: e.target.checked,
                          }))
                        }
                        inputProps={{
                          "aria-labelledby": id,
                          "aria-describedby": `${id}-note`,
                        }}
                        sx={{ p: 0.5 }}
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          id={id}
                          sx={{
                            fontSize: "0.875rem",
                            fontWeight: 600,
                            color: theme.customColors.text.strong,
                          }}
                        >
                          {item.label}
                        </Typography>
                        <Typography
                          id={`${id}-note`}
                          variant="caption"
                          sx={{
                            display: "block",
                            color: theme.customColors.text.secondary,
                          }}
                        >
                          {noteFor(item.key, result, hasLogo)}
                        </Typography>
                      </Box>
                      <Preview item={item} result={result} />
                    </Stack>
                  );
                })}
              </Box>
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={dialogActionsSx(theme)}>
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={applying}
          sx={outlinedButtonSx(theme)}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleApply}
          disabled={!result || !anyChosen || applying || loading}
          sx={primaryButtonSx(theme)}
        >
          {applying ? "Using…" : "Use selected"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FetchBrandingDialog;
